import { Injectable, isDevMode } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { FinOpsAzureSubscriptionsService } from "./finops-azure-subscriptions.service";
import {
    LoadFinOpsAzureSubscriptionsHistoricalData,
    LoadDataTableFinOpsAzureSubscriptions,
    LoadFinOpsAzureSubscriptionsChartsData,
    LoadFinOpsAzureSubscriptionsChartsFilteredData
} from "./finops-azure-subscriptions.actions";
import { finalize, forkJoin } from "rxjs";
import { FinOpsAzureSubscription, FinOpsAzureSubscriptionsStateModel, FinOpsCounters } from "./finops-azure-subscriptions.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { PreferencesFinopsState } from "src/app/store/preferences/finops/preferences-finops.state";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";
import { FinOpsChartHistoricalItem } from "src/app/shared/component/chart-finops-historical/chart-finops-historical.component";
import { FinOpsChartCompareItem } from "src/app/shared/component/chart-finops-compare/chart-finops-compare.component";
import { DateGranularityEnum } from "src/app/shared/enum/date-granularity.enum";
import { ColorEnum } from "src/app/shared/enum/color.enum";
import { AzureSubscriptionLabelPipe } from "src/app/shared/pipe/azure-subscription-label.pipe";
import { TranslateService } from "@ngx-translate/core";
import { DateService, HistoricalDate } from "src/app/shared/service/date.service";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { FloorPipe } from "src/app/shared/pipe/floor.pipe";
import { FormatNumberPipe } from "src/app/shared/pipe/format-number.pipe";
import { MetaService } from "src/app/store/meta/meta.service";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { FinOpsContextState } from "src/app/store/finops/context/finops-context.state";
import { SelectFinOpsDate } from "src/app/store/finops/context/finops-context.actions";

const FINOPS_AZURE_SUBSCRIPTIONS_TOKEN: StateToken<FinOpsAzureSubscriptionsStateModel> = new StateToken('finOpsAzureSubscriptions');

const FinOpsSubscriptionsStateModelDefaults: FinOpsAzureSubscriptionsStateModel = {
    historicalDataByGranularity: {},
    datatable: [],
    chartsDataDisplayed: {
        filteredCompareChartData: {
            minCostRatioString:"",
            minCostValueString: "",
            prediction: false,
            dateString: "",
            costs: []
        },
        filteredHistoricalChartData: {
            minCostRatioString:"",
            minCostValueString: "",
            prediction: false,
            costs: []
        }
    },
    chartsDataByDate: {},
    filteredCounters: {}
}

@State<FinOpsAzureSubscriptionsStateModel>({
    name: FINOPS_AZURE_SUBSCRIPTIONS_TOKEN,
    defaults: FinOpsSubscriptionsStateModelDefaults
})

@Injectable()
export class FinOpsAzureSubscriptionsState {

    private readonly KEY = FINOPS_AZURE_SUBSCRIPTIONS_TOKEN.getName()

    constructor(
        private finOpsAzureSubscriptionsService: FinOpsAzureSubscriptionsService,
        private store: Store,
        private azureSubscriptionLabelPipe: AzureSubscriptionLabelPipe,
        private floorPipe: FloorPipe,
        private numberFormatPipe: FormatNumberPipe,
        private dateService: DateService,
        private translate: TranslateService,
        private metaService: MetaService
    ) {
    }

    @Selector([FINOPS_AZURE_SUBSCRIPTIONS_TOKEN])
    static getDatatable(state: FinOpsAzureSubscriptionsStateModel) {
        return state.datatable
    }

    @Selector([FINOPS_AZURE_SUBSCRIPTIONS_TOKEN])
    static getFilteredCounters(state: FinOpsAzureSubscriptionsStateModel) {
        return state.filteredCounters
    }

    @Selector([FINOPS_AZURE_SUBSCRIPTIONS_TOKEN])
    static getChartsDataDisplayed(state: FinOpsAzureSubscriptionsStateModel) {
        return state.chartsDataDisplayed
    }

    @Selector([FINOPS_AZURE_SUBSCRIPTIONS_TOKEN])
    static getFilteredCompareChartData(state: FinOpsAzureSubscriptionsStateModel) {
        return state.chartsDataDisplayed.filteredCompareChartData
    }

    @Selector([FINOPS_AZURE_SUBSCRIPTIONS_TOKEN])
    static getFilteredHistoricalChartData(state: FinOpsAzureSubscriptionsStateModel) {
        return state.chartsDataDisplayed.filteredHistoricalChartData
    }

    @Selector([FINOPS_AZURE_SUBSCRIPTIONS_TOKEN])
    static getAllChartsDataByDate(state: FinOpsAzureSubscriptionsStateModel) {
        return state.chartsDataByDate
    }

    @Selector([FINOPS_AZURE_SUBSCRIPTIONS_TOKEN])
    static getChartsDataByDate(state: FinOpsAzureSubscriptionsStateModel) {
        return (date:string) => state.chartsDataByDate[date]
    }

    @Selector([FINOPS_AZURE_SUBSCRIPTIONS_TOKEN])
    static getHistoricalDataPerGranularity(state: FinOpsAzureSubscriptionsStateModel) {
        return (granularity: string) => state.historicalDataByGranularity[granularity]
    }

    @Selector([FINOPS_AZURE_SUBSCRIPTIONS_TOKEN])
    static getHistoricalDataPerGranularityPerDate(state: FinOpsAzureSubscriptionsStateModel) {
        return (granularity: string, date: string) => state.historicalDataByGranularity[granularity][date]
    }

    @Action(LoadFinOpsAzureSubscriptionsHistoricalData)
    loadFinOpsAzureSubscriptionsHistoricalData(ctx: StateContext<FinOpsAzureSubscriptionsStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        let historicalDates: HistoricalDate[] = this.dateService.getFinopsHistoricalDates(finopsConfig)
        let selectedDateTile = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)
        let wrapRequestList = []

        for (let historicalDate of historicalDates) {
            const FINOPS_SUBSCRIPTION_DATE_KEY = this.KEY
            + "-granularity-" + finopsConfig.granularity
            + "-date-" + historicalDate.date

            if(!this.metaService.isStale(FINOPS_SUBSCRIPTION_DATE_KEY)) {
                if(isDevMode()) {
                        console.warn("Cache for KEY " + FINOPS_SUBSCRIPTION_DATE_KEY + " still valid, no request executed.")
                }
                continue
            }

            wrapRequestList.push(
                MetaRequestHelper.wrapRequest(
                    this.store,
                    FINOPS_SUBSCRIPTION_DATE_KEY,
                    this.finOpsAzureSubscriptionsService.loadFinOpsAzureSubscriptionsHistoricalData(finopsConfig.granularity, historicalDate.date),
                    resources => {
                        ctx.setState(state => {
                            const h = {...state.historicalDataByGranularity};
                            h[finopsConfig.granularity] = {
                                ...(h[finopsConfig.granularity] || {}),
                                [historicalDate.date]: resources.measures
                            };
                            return {
                                ...state,
                                historicalDataByGranularity: h
                            }
                        })
                    }
                )
            )  
        }
        return forkJoin(wrapRequestList).pipe(
            finalize( () => {
                ctx.dispatch(
                    new LoadDataTableFinOpsAzureSubscriptions()
                )
            }
            )
        )
    }

    @Action(LoadDataTableFinOpsAzureSubscriptions)
    loadDataTableFinOpsAzureSubscriptions(ctx: StateContext<FinOpsAzureSubscriptionsStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        const granularity = finopsConfig.granularity
        const historicalDatesSelected = this.dateService.getFinopsHistoricalDates(finopsConfig).map(item => item.date)
        const selectedDateTile = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)

        // If cannot find selected date in historical dates, select the last one and load data for it
        if (selectedDateTile === undefined || !historicalDatesSelected.includes(selectedDateTile)) {
            ctx.dispatch([
                new SelectFinOpsDate(historicalDatesSelected[historicalDatesSelected.length - 1]),
                new LoadDataTableFinOpsAzureSubscriptions()
            ])
            return
        }

        if (isDevMode()) {
            console.warn("Loading data for granularity " + granularity + " and date " + selectedDateTile)
        }

        let historicalDataByGranularity = ctx.getState().historicalDataByGranularity[granularity]
        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AZURE_SUBSCRIPTIONS]]

        let measuresDatatable: { [id: string]: FinOpsAzureSubscription[]; } = {}
        let filteredCounters: { [id: string]: Record<string, FinOpsCounters>; } = {}
        let previousTotalCounters: { [id: string]: FinOpsCounters; } = {}

        for(let date of historicalDatesSelected){
                let datatable: FinOpsAzureSubscription[] = []
                Object.values(historicalDataByGranularity[date]).forEach(subscription => {
                    datatable.push(subscription)
                });
                filters.forEach(filter => {
                    if (Array.isArray(filter.value)) {
                        datatable = datatable.filter((subscription) => filter.value.includes(subscription[filter.key as keyof FinOpsAzureSubscription]))
                    } else {
                        datatable = datatable.filter((subscription) => subscription[filter.key as keyof FinOpsAzureSubscription] === filter.value)
                    }
                });
                measuresDatatable[date] = datatable

                // Update counters for the measure
                let totalCounters: Record<DateGranularityEnum, FinOpsCounters> = {
                    year: {
                        prediction: false,
                        previous_cost: 0,
                        cost: 0,
                        day_ratio: 0,
                        gap_ratio: 0,
                        gap_value: 0
                    },
                    month: {
                        prediction: false,
                        previous_cost: 0,
                        cost: 0,
                        day_ratio: 0,
                        gap_ratio: 0,
                        gap_value: 0
                    },
                    day: {
                        prediction: false,
                        previous_cost: 0,
                        cost: 0,
                        day_ratio: 0,
                        gap_ratio: 0,
                        gap_value: 0
                    }
                }
                datatable.forEach(row => {
                    Object.entries(row.costs_counters).forEach(entries => {
                        let granularity = entries[0] as DateGranularityEnum
                        let counters = entries[1]
                        totalCounters[granularity].prediction = counters.prediction
                        totalCounters[granularity].cost += Number(counters.cost)
                        totalCounters[granularity].day_ratio = Number(counters.day_ratio)
                    });
                });

                Object.keys(totalCounters).forEach(granularity  => {
                    totalCounters[granularity as DateGranularityEnum].cost = totalCounters[granularity as DateGranularityEnum].cost
                    totalCounters[granularity as DateGranularityEnum].day_ratio = totalCounters[granularity as DateGranularityEnum].day_ratio

                    if (Object.keys(previousTotalCounters).length > 0) {
                        totalCounters[granularity as DateGranularityEnum].previous_cost = previousTotalCounters[granularity].cost
                        let gap_value = totalCounters[granularity as DateGranularityEnum].cost - totalCounters[granularity as DateGranularityEnum].previous_cost
                        totalCounters[granularity as DateGranularityEnum].gap_ratio = totalCounters[granularity as DateGranularityEnum].previous_cost == 0 ? 100 : ((gap_value / totalCounters[granularity as DateGranularityEnum].previous_cost) * 100)
                        totalCounters[granularity as DateGranularityEnum].gap_value = gap_value
                    }
                });
                filteredCounters[date] = totalCounters
                previousTotalCounters = totalCounters
            }

        ctx.patchState({
            datatable: measuresDatatable[selectedDateTile],
            filteredCounters: filteredCounters
        })
    }

    @Action(LoadFinOpsAzureSubscriptionsChartsData)
    loadFinOpsAzureSubscriptionsChartsData(ctx: StateContext<FinOpsAzureSubscriptionsStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        let historicalDates: HistoricalDate[] = this.dateService.getChartsHistoricalDates(chartsConfig)
        let wrapRequestList = []
        for (let historicalDate of historicalDates) {
            const FINOPS_SUBSCRIPTION_CHARTS_DATE_KEY = this.KEY
            + "-charts"
            + "-date-" + historicalDate.date

            if(!this.metaService.isStale(FINOPS_SUBSCRIPTION_CHARTS_DATE_KEY)) {
                if(isDevMode()) {
                    console.warn("Cache for KEY " + FINOPS_SUBSCRIPTION_CHARTS_DATE_KEY + " still valid, no request executed.")
                }
                continue
            }

            wrapRequestList.push(
                MetaRequestHelper.wrapRequest(
                    this.store,
                    FINOPS_SUBSCRIPTION_CHARTS_DATE_KEY,
                    this.finOpsAzureSubscriptionsService.loadFinOpsAzureSubscriptionsMeasures(historicalDate.date),
                    chart_resources => {
                        ctx.setState(state => {
                            return {
                                ...state,
                                chartsDataByDate: {
                                    ...state.chartsDataByDate,
                                    [historicalDate.date]: chart_resources.measures
                                }
                            }
                        })
                    }
                )
            )
        }
        return forkJoin(wrapRequestList).pipe(
            finalize(() => {
                ctx.dispatch(
                    new LoadFinOpsAzureSubscriptionsChartsFilteredData()
                )
            })
        )
    }

    @Action(LoadFinOpsAzureSubscriptionsChartsFilteredData)
    loadFinOpsAzureSubscriptionsChartsFilteredData(ctx: StateContext<FinOpsAzureSubscriptionsStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const granularity = chartsConfig.granularity
        const bulkChartsData = ctx.getState().chartsDataByDate
        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AZURE_SUBSCRIPTIONS_CHARTS]]

        //Required for charts data computing
        const historicalDatesSelected = this.dateService.getChartsHistoricalDates(chartsConfig).map(item => item.date)
        const computeChartDataLastDate = historicalDatesSelected[historicalDatesSelected.length -1]

        // Init all section
        let historicalChartMinCostRatio: number = 0.0
        let historicalChartMinCostValue: number = 0.0
        let historicalChartCosts: FinOpsChartHistoricalItem[] = []
        let compareMinCostRatio: number = 0.01
        let compareMinCostValue: number = 0.0
        let compareChartCosts: FinOpsChartCompareItem[] = []
        let prediction: boolean = false

        Object.keys(bulkChartsData).sort().forEach( (dateKey) => {
                let filteredSubscriptions: FinOpsAzureSubscription[] = []
                bulkChartsData[dateKey].forEach(measure => {
                    filteredSubscriptions.push(measure)
                })
                filters.forEach(filter => {
                    if(Array.isArray(filter.value)) {
                        filteredSubscriptions = filteredSubscriptions.filter((subscription) => filter.value.includes(subscription[filter.key as keyof FinOpsAzureSubscription]))
                    } else {
                        filteredSubscriptions = filteredSubscriptions.filter((subscription) => subscription[filter.key as keyof FinOpsAzureSubscription] === filter.value)
                    }
                })

                // Compute historical chart data for the current date
                let totalCosts = 0
                prediction = false
                filteredSubscriptions.forEach(subscription => {
                    totalCosts += Number(subscription.costs_counters[granularity].cost)
                    prediction = subscription.costs_counters[granularity].prediction
                });

                // Only compute dates concerned by context
                if(historicalDatesSelected.includes(dateKey)) {
                    historicalChartCosts.push(
                        {
                            date: new Date(dateKey),
                            cost: parseFloat(this.floorPipe.transform(totalCosts, 2)),
                            dateString: this.dateService.formatLongDate(dateKey),
                            predictionString: prediction ? this.translate.instant("FINOPS.PREDICTION") + "\n\n" : ""
                        }
                    )
                }

                // Compute compare charts data only for the last date
                if (dateKey == computeChartDataLastDate) {
                    compareMinCostValue = totalCosts * compareMinCostRatio
                    filteredSubscriptions.forEach(subscription => {
                        let subscriptionCost = Number(subscription.costs_counters[granularity].cost)
                        if (subscriptionCost > compareMinCostValue) {
                            let colorKey = 'charts_color_' + compareChartCosts.length
                            compareChartCosts.push(
                                {
                                    category: this.azureSubscriptionLabelPipe.transform(subscription.id),
                                    cost: parseFloat(this.floorPipe.transform(subscriptionCost, 2)),
                                    color: ColorEnum[colorKey as keyof typeof ColorEnum]
                                }
                            )
                        }
                    });
                }
            }
        )

        ctx.patchState({
            chartsDataDisplayed: {
                filteredHistoricalChartData: {
                    minCostRatioString: historicalChartMinCostRatio == 0 ? "" : this.numberFormatPipe.transform(this.floorPipe.transform(historicalChartMinCostRatio * 100, 2))!,
                    minCostValueString: historicalChartMinCostValue == 0 ? "" : this.numberFormatPipe.transform(this.floorPipe.transform(historicalChartMinCostValue, 2))!,
                    prediction: prediction,
                    costs: historicalChartCosts
                },
                filteredCompareChartData: {
                    minCostRatioString: compareMinCostRatio == 0 ? "" : this.numberFormatPipe.transform(this.floorPipe.transform(compareMinCostRatio * 100, 2))!,
                    minCostValueString: compareMinCostValue == 0 ? "" : this.numberFormatPipe.transform(this.floorPipe.transform(compareMinCostValue, 2))!,
                    dateString: this.dateService.formatLongDate(computeChartDataLastDate),
                    prediction: prediction,
                    costs: compareChartCosts
                }
            }
        })
    }
}
